import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MailX, MessageSquareQuote, Users } from 'lucide-react';
import { unsubscribeReasonLabel } from '@blogforge/shared';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import { formatDate } from '../lib/billing-format.js';

// ─────────────────────────────────────────────────────────────────
// Unsubscribes — who opted out, of what, and why they said so.
//
// The other half of the public opt-out page: it removes someone from a list
// and records the answer they gave, and this is where that answer is read.
// Read-only on purpose. An opt-out is a request, not a list membership, so
// nothing here puts anyone back — the record is what keeps a re-imported CSV
// from silently re-subscribing them.
//
// The three filters are the three questions worth asking of this data: who
// left everything (scope=workspace), who left one list (listId), and what did
// they write (the feedback shown inline on every row).
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

/** The filter is one choice, like the scope it maps onto: all, sender-wide, or one list. */
type ScopeFilter = { kind: 'all' } | { kind: 'workspace' } | { kind: 'list'; listId: string };

export function UnsubscribesPage() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const [filter, setFilter] = useState<ScopeFilter>({ kind: 'all' });
  const [page, setPage] = useState(1);

  const listsQuery = useQuery({
    queryKey: ['email-lists', activeWorkspaceId],
    queryFn: () => api.emailLists.list(),
    enabled: Boolean(activeWorkspaceId),
  });

  const query = useQuery({
    queryKey: [
      'unsubscribes',
      activeWorkspaceId,
      filter.kind,
      filter.kind === 'list' ? filter.listId : '',
      page,
    ],
    queryFn: () =>
      api.unsubscribes.list({
        page,
        limit: PAGE_SIZE,
        ...(filter.kind === 'workspace' ? { scope: 'workspace' as const } : {}),
        ...(filter.kind === 'list' ? { listId: filter.listId } : {}),
      }),
    enabled: Boolean(activeWorkspaceId),
  });

  // The dropdown's value, which is also how the filter is chosen.
  const filterValue = useMemo(() => {
    if (filter.kind === 'all') return 'all';
    if (filter.kind === 'workspace') return 'workspace';
    return `list:${filter.listId}`;
  }, [filter]);

  const rows = query.data?.data ?? [];
  const lists = listsQuery.data?.data ?? [];

  const selectFilter = (value: string) => {
    setPage(1);
    if (value === 'all') return setFilter({ kind: 'all' });
    if (value === 'workspace') return setFilter({ kind: 'workspace' });
    if (value.startsWith('list:')) return setFilter({ kind: 'list', listId: value.slice(5) });
    setFilter({ kind: 'all' });
  };

  return (
    <div className="pb-10">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3 px-8 pt-8 pb-5">
        <div>
          <p className="kicker mb-2">
            <span className="bg-accent h-1.5 w-1.5 rounded-full" />
            Audience
          </p>
          <h1 className="text-ink text-[22px] font-bold tracking-tight">Unsubscribes</h1>
          <p className="text-ink-soft mt-1 max-w-2xl text-[13px]">
            Everyone who opted out of your emails, what they left, and what they told you on the way
            out. These addresses are excluded from future sends to the lists they left — and stay
            excluded if the same contacts are imported again.
          </p>
        </div>

        <label className="flex items-center gap-2 text-[12.5px]">
          <span className="text-muted font-medium">Showing</span>
          <select
            value={filterValue}
            onChange={(e) => selectFilter(e.target.value)}
            className="field-input text-ink cursor-pointer py-1.5 pr-7 text-[12.5px] font-medium"
          >
            <option value="all">All opt-outs</option>
            <option value="workspace">Whole sender stopped</option>
            {lists.map((list) => (
              <option key={list.id} value={`list:${list.id}`}>
                Left “{list.name}”
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* ── Rows ────────────────────────────────────────────── */}
      {query.isLoading ? (
        <div className="text-muted flex h-32 items-center justify-center gap-2 px-8 text-sm">
          <span className="border-line-strong border-t-accent h-3.5 w-3.5 animate-spin rounded-full border-2" />
          Loading opt-outs…
        </div>
      ) : query.isError ? (
        <p className="border-danger/20 bg-danger/8 text-danger mx-8 rounded-lg border px-3 py-2 text-[12.5px]">
          {query.error instanceof Error ? query.error.message : 'Could not load opt-outs.'}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState filtered={filter.kind !== 'all'} />
      ) : (
        <div className="space-y-2.5 px-8">
          {rows.map((row) => (
            <article key={row.id} className="surface-row px-4 py-3.5">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <span className="text-ink font-mono text-[13px] font-semibold">{row.email}</span>
                <ScopeBadge row={row} />
                {row.reason && (
                  <span className="text-ink-soft bg-ink/5 rounded-full px-2 py-0.5 text-[11px] font-medium">
                    {unsubscribeReasonLabel(row.reason)}
                  </span>
                )}
                <span className="text-muted ml-auto text-[11.5px] whitespace-nowrap">
                  {formatDate(row.createdAt)}
                </span>
              </div>

              {row.feedback && (
                <p className="border-line text-ink-soft mt-2.5 flex items-start gap-2 border-l-2 pl-3 text-[12.5px] leading-relaxed">
                  <MessageSquareQuote size={13} className="text-muted mt-0.5 shrink-0" />
                  <span>{row.feedback}</span>
                </p>
              )}

              <p className="text-muted mt-2 text-[11.5px]">
                {row.emailSubject ? (
                  <>
                    Opted out from{' '}
                    <span className="text-ink-soft font-medium">{row.emailSubject}</span>
                  </>
                ) : (
                  'Opted out from an email that has since been deleted'
                )}
              </p>
            </article>
          ))}

          {/* ── Pagination ────────────────────────────────────── */}
          <div className="text-muted flex items-center justify-between px-1 pt-2 text-[12px]">
            <span>
              Showing {(query.data!.page - 1) * query.data!.limit + 1}–
              {Math.min(query.data!.page * query.data!.limit, query.data!.total)} of{' '}
              {query.data!.total.toLocaleString()}
            </span>
            {query.data!.totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="btn-ghost px-2 py-1"
                >
                  <ChevronLeft size={13} />
                  Prev
                </button>
                <span className="px-1 tabular-nums">
                  {query.data!.page}/{query.data!.totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(query.data!.totalPages, p + 1))}
                  disabled={page >= query.data!.totalPages}
                  className="btn-ghost px-2 py-1"
                >
                  Next
                  <ChevronRight size={13} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * What they left: one list by name, or everything this sender sends. The list
 * name is the one on the row, not a lookup — a list deleted after the opt-out
 * takes its membership with it (the FK cascades), so a row can outlive the
 * name it was recorded against.
 */
function ScopeBadge({ row }: { row: { scope: 'list' | 'workspace'; listName: string | null } }) {
  if (row.scope === 'workspace') {
    return (
      <span className="bg-accent/10 text-accent rounded-full px-2 py-0.5 text-[10.5px] font-semibold shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
        All emails
      </span>
    );
  }
  return (
    <span className="text-ink-soft bg-ink/5 rounded-full px-2 py-0.5 text-[10.5px] font-semibold">
      {row.listName ?? 'A deleted list'}
    </span>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
      <div className="bg-accent/10 text-accent flex h-16 w-16 items-center justify-center rounded-2xl shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
        {filtered ? <Users size={26} strokeWidth={1.8} /> : <MailX size={26} strokeWidth={1.8} />}
      </div>
      <h2 className="text-ink mt-5 text-[18px] font-bold tracking-tight">
        {filtered ? 'Nobody here' : 'Nobody has unsubscribed'}
      </h2>
      <p className="text-muted mt-2 max-w-md text-[13px] leading-relaxed">
        {filtered ? (
          'No opt-outs match this filter. Try another list, or show all opt-outs.'
        ) : (
          <>
            Every email you send carries an unsubscribe link; opt-outs land here with whatever
            reason the reader chose. When one arrives you will see it on this page — and the address
            stops receiving that list straight away.
          </>
        )}
      </p>
      {!filtered && (
        <Link to="/emails/lists" className="btn-ghost mt-4 px-4 py-2 text-[13px]">
          <Users size={14} />
          Back to lists
        </Link>
      )}
    </div>
  );
}
